import { CreateQueryParams } from '@libs/crud-request-nestjs';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';

import { Builder } from '../../../shared/_models/builder';
import {
  AppFilter,
  AppTable,
  AppTableColumn,
  DropdownButton,
  DropdownButtonsConfig,
  MainDropdownButton,
  NestAuditAction,
  NestAuditEntityName,
  AuthPermissions,
} from '../../../shared/_models/models';
import { RxFormForecastSubmitActionType } from '../../exchange-forecasts/_services/exchange-forecasts.config';
import { ExchangeAuditConfig } from '../../../shared/_components/exchange-audit/exchange-audit.component';
import { ExchangeAuditDetailsComponent } from '../../../shared/_components/exchange-audit/exchange-audit-details/exchange-audit-details.component';
import { DateHelper } from '../../../shared/_common/date.helper';

dayjs.extend(utc);
dayjs.extend(timezone);

export const exchangeDealsTableConfig: AppTable = new AppTable('exchangeDealsTable', 1, 10, 'createdAt', 'DESC');

export const exchangeDealsFiltersConfig: AppFilter[] = [
  new AppFilter('exchangeDealsStatusSFilter', 'SELECT', 'status', 'Status', 'Select status',
    'multiple', true, true, null,
    [{ id: 1, displayName: 'Active', value: 'ACTIVE', isDisabled: false },
      { id: 2, displayName: 'Inactive', value: 'INACTIVE', isDisabled: false }],
    [], null, null, null, null,
    [{ id: 1, displayName: 'Active', value: 'ACTIVE', isDisabled: false }]),
  new AppFilter('exchangeDealsPhaseFilter', 'SELECT', 'phase', 'Phase', 'Select phase',
    'default', true, true, null,
    [{ id: 1, displayName: 'Live', value: 'LIVE' },
      { id: 2, displayName: 'Scheduled', value: 'SCHEDULED' },
      { id: 3, displayName: 'Completed', value: 'COMPLETED' }],
    [{ id: 1, displayName: 'Live', value: 'LIVE' }], null,
    null, null, null,
    [{ id: 1, displayName: 'Live', value: 'LIVE' }]),
  new AppFilter('exchangeDealSearchFilter', 'SEARCH', 'searchExchangeDeal', 'Search Exchange Deal',
    'Search by deal id/name/description', 'default', true, true, null),
  new AppFilter('exchangeDealsPersonnelFilter', 'SELECT', 'String', 'Personnel', 'Filter by personnel',
    'default', true, false, null,
    [], []),
  new AppFilter('exchangeDealsAccountTypeFilter', 'SELECT', 'String', 'Account', 'Filter by account',
    'multiple', true, false, null, [], [],
    null, false, null, false, [{ id: 0, displayName: 'Nexxen', value: 'Nexxen' }],
    '', false, true),
  new AppFilter(
    'exchangeDealTypeFilter', 'SELECT_WITH_GROUPS', 'String', 'Deal Type',
    'Filter by type', 'multiple', true, false, null, [], [],
    null, null, null, false, undefined, undefined, undefined,
    true, undefined, undefined, undefined, undefined, undefined,
    undefined, undefined, undefined, undefined, undefined, undefined,
    undefined, []
  ),
  new AppFilter('exchangeDealsDSPFilter', 'SELECT_WITH_GROUPS', 'field', 'DSP', 'Filter by dsp',
    'multiple', true, false, null,
    [], [],
    null, null, null, false, undefined, undefined, undefined,
    true, undefined, undefined, undefined, undefined, undefined, undefined,
    undefined, undefined, undefined, undefined, undefined, undefined, []),
];

export const exchangeDealsPartnerFiltersConfig: AppFilter[] = [
  new AppFilter('exchangeDealsStatusSFilter', 'SELECT', 'status', 'Status', 'Select status',
    'multiple', true, true, null,
    [{ id: 1, displayName: 'Active', value: 'ACTIVE', isDisabled: false },
      { id: 2, displayName: 'Inactive', value: 'INACTIVE', isDisabled: false }],
    [], null, null, null, null,
    []),
  new AppFilter('exchangeDealsPhaseFilter', 'SELECT', 'phase', 'Phase', 'Select phase',
    'default', true, true, null,
    [{ id: 1, displayName: 'Live', value: 'LIVE' },
      { id: 2, displayName: 'Scheduled', value: 'SCHEDULED' },
      { id: 3, displayName: 'Completed', value: 'COMPLETED' }],
    [], null, null, null, null, []),
  new AppFilter('exchangeDealSearchFilter', 'SEARCH', 'searchExchangeDeal', 'Search Curated Marketplace Deal',
    'Search by deal id/name', 'default', true, true, null),
  new AppFilter('exchangeDealsDSPFilter', 'SELECT', 'field', 'DSP', 'Filter by dsp',
    'multiple', true, false, null, [], [])
];

export const exchangeDealsTableButtons = [
  { id: 'exchangeDealsCreateButton', type: 'PRIMARY-BUTTON', title: 'Create Exchange Deal', hasLoadingSpinner: false, auth: 'canCreate' }
];

export const exchangeDealsPartnerTableButtons = [
  // eslint-disable-next-line max-len
  { id: 'exchangeDealsCreateButton', type: 'PRIMARY-BUTTON', title: 'Create Deal', hasLoadingSpinner: false, auth: 'canCreate' }
];

export const exchangeDealsTableColumns: AppTableColumn[] = [
  {
    id: 'exchangeDealsStatus', prop: 'status', displayName: 'Status', type: 'tags-rx-dashboard', isSortEnabled: false,
    colWidth: '100px',
    tags: [
      { id: 'exchangeDealTagActive', name: 'Active', condition: 'active', tooltip: 'Active' },
      { id: 'exchangeDealTagInactive', name: 'Inactive', condition: 'inactive', tooltip: 'Inactive' },
    ]
  },
  {
    id: 'exchangeDealsExternalDealId', prop: 'externalDealId', displayName: 'Deal ID',
    type: 'text', isSortEnabled: true, maxWidth: '130px'
  },
  {
    id: 'exchangeDealNameDescription',
    prop: 'description',
    displayName: 'Description/Name',
    type: 'custom-function',
    isSortEnabled: true,
    valueFormatter: (val, row) => (val && val.trim()) ? val.trim() : row.name
  },
  { id: 'exchangeDealsAgency', prop: 'agency.name', displayName: 'Agency', type: 'text', isSortEnabled: true, colWidth: '150px' },
  { id: 'exchangeDealsBrand', prop: 'dealBrand.name', displayName: 'Brand', type: 'text', isSortEnabled: true, colWidth: '110px' },
  { id: 'exchangeDealsStartDate', prop: 'dealStart', displayName: 'Start Date', type: 'date', isSortEnabled: true },
  { id: 'exchangeDealsEndDate', prop: 'dealEnd', displayName: 'End Date', type: 'date', isSortEnabled: true },
  {
    id: 'exchangeDealsSspAccountDisplayName', prop: 'ssps', displayName: 'SSP', type: 'moreTag',
    isSortEnabled: false,
    moreTag: { displayNameProp: 'name', popoverTitle: 'More SSP' }
  },
  {
    id: 'exchangeDealsDspAccountDisplayName', prop: 'dspAccounts', displayName: 'DSP', type: 'moreTag',
    isSortEnabled: false,
    moreTag: { displayNameProp: 'nameDisplay', popoverTitle: 'More DSP' }
  },
  {
    id: 'dealsActions', prop: 'actions', displayName: 'Actions', type: 'actions',
    actions: [
      { id: 'exchangeDealEdit', name: 'edit', auth: 'canUpdate', displayName: 'Edit', hrefLink: '/edit', hrefIdProp: 'dealId' },
      // {id: 'exchangeDealDuplicate', name: 'duplicate', auth: 'canUpdate', displayName: 'Duplicate'},
    ]
  }
];

export const exchangeDealsPartnerTableColumns: AppTableColumn[] = [
  {
    id: 'exchangeDealsStatus', prop: 'status', displayName: 'Status', type: 'tags-rx-dashboard', isSortEnabled: false,
    colWidth: '100px',
    tags: [
      { id: 'exchangeDealTagActive', name: 'Active', condition: 'active', tooltip: 'Active' },
      { id: 'exchangeDealTagInactive', name: 'Inactive', condition: 'inactive', tooltip: 'Inactive' },
    ]
  },
  {
    id: 'exchangeDealsExternalDealId', prop: 'externalDealId', displayName: 'Deal ID',
    type: 'text', isSortEnabled: true, maxWidth: '130px'
  },
  { id: 'exchangeDealNameDescription', prop: 'name', displayName: 'Name', type: 'text', isSortEnabled: true },
  { id: 'exchangeDealsStartDate', prop: 'dealStart', displayName: 'Start Date', type: 'date', isSortEnabled: true },
  { id: 'exchangeDealsEndDate', prop: 'dealEnd', displayName: 'End Date', type: 'date', isSortEnabled: true },
  {
    id: 'exchangeDealsDspAccountDisplayName', prop: 'dspAccounts', displayName: 'DSP', type: 'moreTag',
    isSortEnabled: false,
    moreTag: { displayNameProp: 'nameDisplay', popoverTitle: 'More DSP' }
  },
  {
    id: 'dealsActions', prop: 'actions', displayName: 'Actions', type: 'actions',
    actions: [
      { id: 'exchangeDealEdit', name: 'edit', auth: 'canUpdate', displayName: 'Edit', hrefLink: '/edit', hrefIdProp: 'dealId' },
    ]
  }
];

export const initialExchangeDealsFilterQueryConfig: CreateQueryParams = {
  search: {
    status: { $in: ['ACTIVE'] },
  },
  page: 1,
  sort: [ {
    field: 'dealEnd',
    order: 'ASC'
  }],
  limit: 10
};

export const initialExchangeSegmentsFilterQueryConfig: CreateQueryParams = {
  search:
    {
      status: { $eq: 'active' },
    }
  ,
  page: 1,
  sort: [{
    field: 'segmentAudience.name',
    order: 'ASC'
  }, {
    field: 'displayName',
    order: 'ASC'
  },
  ],
  limit: 25
};

export const universalMediaListsFilterQueryParams: CreateQueryParams = {
  page: 1,
  limit: 25,
  join: [{ field: 'segment' }],
  search: {
    'segment.status': { $eq: 'active' },
    'listType': { $in: ['shared', 'curated_marketplace'] }
  }
};

export const universalMediaListsFilterInternalQueryParams: CreateQueryParams = {
  page: 1,
  limit: 25,
  join: [{ field: 'segment' }],
  search: {
    'segment.status': { $eq: 'active' },
    'listType': { $in: ['internal', 'shared'] }
  }
};

export const rxSegmentAddIncludeButton: DropdownButtonsConfig = {
  main: { id: 'addInclusionGroup', label: 'Add Inclusion Group', buttonType: 'primary-button' },
  options: [
    { id: 'addInclusionGroupMedia', label: 'Add Media' },
  ],
};

export const rxSegmentAddExcludeButton: DropdownButtonsConfig = {
  main: { id: 'addExclusionGroup', label: 'Add Exclusion Group', buttonType: 'secondary-button' },
  options: [
    { id: 'addExclusionGroupMedia', label: 'Add Media' },
  ],
};

export type RxFormDealSubmitActionId =
  'createDeal' | 'updateDeal' | 'createForecast' |
  'updateForecast' | 'syncDeal' | 'runForecast' |
  'cloneDeal' | 'convertToDeal' | 'convertToForecast' |
  'cloneDealApproved' | 'convertToDealApproved';

export type RxFormDealSubmitActionType = 'create'|'update'|'clone'|'sync'|'convert';

export interface RxFormSubmitActionButtonConfig {type: 'main'|'option'; config: MainDropdownButton | DropdownButton}

export const rxFormDealSubmitButtonConfigs: Partial<Record<RxFormDealSubmitActionType, RxFormSubmitActionButtonConfig>> = {
  create: {
    type: 'main',
    config: { id: 'createDeal', label: 'Create Deal', buttonType: 'primary-button' }
  },
  update: {
    type: 'main',
    config: { id: 'updateDeal', label: 'Save Deal', buttonType: 'primary-button' }
  },
  sync: {
    type: 'option',
    config: { id: 'syncDeal', label: 'Save and Sync' },
  },
  clone: {
    type: 'option',
    config: { id: 'cloneDeal', label: 'Save and Clone' },
  },
  convert: {
    type: 'option',
    config: { id: 'convertToForecast', label: 'Save and Run Forecast' },
  }
};

export const getRxFormDealSubmitButtonConfigsSyncPrimary = (
  mode: 'create' | 'update',
): Partial<Record<RxFormDealSubmitActionType, RxFormSubmitActionButtonConfig>> => ({
  ...rxFormDealSubmitButtonConfigs,
  create: { ...rxFormDealSubmitButtonConfigs.create!, type: 'option' },
  update: { ...rxFormDealSubmitButtonConfigs.update!, type: 'option' },
  sync: {
    type: 'main',
    config: {
      id: 'syncDeal',
      label: mode === 'create' ? 'Create and Sync' : 'Save and Sync',
      buttonType: 'primary-button',
    },
  },
});

export const getRxDealFormSubmitDropdownButtonConfig = (
  configs: Partial<Record<(RxFormDealSubmitActionType | RxFormForecastSubmitActionType), RxFormSubmitActionButtonConfig>>,
  actions: (RxFormDealSubmitActionType | RxFormForecastSubmitActionType)[]
): DropdownButtonsConfig => {
  const dropdownButtonConfig: DropdownButtonsConfig = { main: null, options: [] };
  actions.forEach(action => {
    if (configs[action].type === 'main') {
      dropdownButtonConfig.main = configs[action].config as MainDropdownButton;
    } else if (configs[action].type === 'option') {
      dropdownButtonConfig.options.push(configs[action].config);
    }
  });

  if (!dropdownButtonConfig.main && dropdownButtonConfig.options.length === 1) {
    const option = dropdownButtonConfig.options[0];
    dropdownButtonConfig.main = {
      id: option.id,
      label: option.label,
      buttonType: 'primary-button'
    };
    dropdownButtonConfig.options = [];
  }

  return dropdownButtonConfig;
};

export const errorDictionary: { [key: string]: string } = {
  viewabilityFiltersError: 'Viewability filter must be a decimal number between 1 and 100',
  unrulyBundledDealError: 'You must select at least one Nexxen bundled deal.',
  sspDealListError: 'Please select at least one ssp deal.',
  sspListError: 'Please select at least one ssp.',
  demandMarginError: 'Demand margin can\'t be empty if there are additional demand margins',
  zeroNotAllowedError: '\'0\' not allowed.',
};

export const exchangeDealsDataMock = {
  data: {
    items: [
      {
        'overallImpCap': null,
        'name': '',
        'auctionType': 'donotset',
        'dealFloors': [{
          'dealId': 20716,
          'dspAccountId': 5130,
          'minPrice': 20
        }],
        'dealExternalSyncs': [],
        'priorityId': 3,
        'externalDealId': '1R-RX1-04508-20220516400760',
        'dealId': 20716,

        'wseat': '',
        'demandMarginValue': 25,
        'dataFee': 0,
        'bidfloor': 20,
        'dealEnd': '2024-01-16T21:16:18.000Z',
        'dealStart': '2022-04-30T21:00:00.000Z',
        'description': 'TTD-Gale-VeteransUnited_United2020_CTV_WL-inclusion_Floor_$20',
        'status': 'active',
        'dspAccounts': [{
          'id': 5130,
          'name': 'The Trade Desk'
        }],
        'ssps': [{
          'id': 2057,
          'name': 'Unruly - 1R ZONE'
        }, {
          'id': 2064,
          'name': 'Unruly - HB'
        }, {
          'id': 2069,
          'name': 'Unruly - Google OB'
        }, {
          'id': 2118,
          'name': 'Unruly - OpenRTB'
        }, {
          'id': 2129,
          'name': 'Unruly - Outstream'
        }, {
          'id': 2153,
          'name': 'Unruly - OpenRTB (select ctv pubs)'
        }],
        'dealBrand': {
          'id': 1335,
          'name': 'Veterans United'
        },
        'agency': {
          'id': 877,
          'name': 'GALE Partners'
        },
        'impType': 'video',
        'mediaType': ['app'],
        'createdAt': '2022-05-16T09:46:59.000Z',
        'updatedAt': '2022-12-01T10:14:30.000Z',
        'deviceType': ['settopbox', 'connectedtv'],
        'geoTargeting': {
          'countries': ['US']
        }
      },
      {
        name: 'sagi_deal_2',
        dealStart: '2022-09-19T22:00:00.000Z',
        externalDealId: '1R-RX1-09187-20220712345678',
        dealId: 22045,
        agency: 'Yahoo',
        brand: 'Walmart',
        wseat: 'wseat1,wseat2',
        demandMarginValue: 25,
        dataFee: 2.6785,
        bidfloor: 3.3165,
        dealEnd: '2023-09-25T22:00:00.000Z',
        description: 'RX_Deal_Test-API_ver2',
        status: 'active',

        dspAccounts: [
          {
            id: 5069,
            name: 'Tremor',
          },
          {
            id: 5014,
            name: 'Mediamath',
          }
        ],
        ssps: [
          {
            id: 2093,
            name: 'IronSource',
          },
          {
            id: 2091,
            name: 'Taboola',
          }
        ],
        viewabilityPercentage: 33,
        impType: 'video',
        mediaType: [
          'site',
          'app'
        ],
        createdAt: '2022-07-24T22:34:32.788Z',
        updatedAt: null,
        deviceType: [
          'desktop',
          'mobile'
        ],
        geoTargeting: {
          countries: [
            'US'
          ],
          regions: [
            'al',
            'de'
          ],
          metros: [
            'new york',
            'detroit'
          ],
          zipCodes: [
            'A1A2A3',
            'B1B2B3'
          ]
        }
      }
    ],
    meta: {
      totalItems: 2,
      itemCount: 2,
      itemsPerPage: 10,
      totalPages: 1,
      currentPage: 1
    }
  }
};

export const getExchangeDealAuditConfig = (authPermissions?: AuthPermissions): ExchangeAuditConfig => {
  const today = DateHelper.getTodayInEasternTime().endOf('day');
  const dateRange = [today.startOf('day').format('YYYY-MM-DD HH:mm:ss'), today.format('YYYY-MM-DD HH:mm:ss')];

  const exchangeDealAuditActionFilterValues = [
    { id: 1, displayName: 'Create', value: NestAuditAction.Create, isDisabled: false },
    { id: 2, displayName: 'Update', value: NestAuditAction.Update, isDisabled: false },
    { id: 3, displayName: 'Delete', value: NestAuditAction.Delete, isDisabled: false }
  ];

  const exchangeDealAuditFilters: AppFilter[] = [
    Builder.create(AppFilter, 'exchangeDealRangeFilter', 'DATES_RANGE', 'timestamp', 'Date Range (EST)', '')
      .allowClear(false)
      .isServerSearch(true)
      .serverSearchProps('timestamp')
      .selectedValues([...dateRange])
      .defaultValues([...dateRange])
      .errorTooltip('Date range is required')
      .sendParamAs('query')
      .build(),
    Builder.create(AppFilter, 'exchangeDealAuthorSearchFilter', 'SEARCH', 'author', 'author ID/Name', 'Search by author id/name')
      .selectMode('default')
      .allowClear(true)
      .isServerSearch(true)
      .serverSearchProps('author')
      .build(),
    Builder.create(AppFilter, 'exchangeDealActionSelectFilter', 'SELECT', 'action', 'Action', 'Select status')
      .selectMode('multiple')
      .allowClear(true)
      .isServerSearch(false)
      .possibleValues(exchangeDealAuditActionFilterValues)
      .selectedValues(exchangeDealAuditActionFilterValues)
      .defaultValues([])
      .build(),
  ];

  const exchangeDealAuditTableColumns: AppTableColumn[] = [
    {
      id: 'exchangeDealAuditExpandRow',
      prop: '',
      displayName: '',
      type: 'expand-row',
      actionId: 'exchangeDealAuditExpandRow',
      isSortEnabled: false,
      colWidth: '60px',
    },
    { id: 'timestamp', prop: 'timestamp', displayName: 'Timestamp', type: 'date-and-time-timestamp-to-est', isSortEnabled: true },
    { id: 'author', prop: 'author', displayName: 'Author', type: 'text', isSortEnabled: true },
    { id: 'action', prop: 'action', displayName: 'Action', type: 'titlecase-text', isSortEnabled: true },
  ];

  return {
    entityName: NestAuditEntityName.ExchangeDeal,
    tableConfig: new AppTable('exchangeDealAuditTable', 1, 10, 'createdAt', 'DESC', true),
    tableColumns: exchangeDealAuditTableColumns,
    filters: exchangeDealAuditFilters,
    initialQueryConfig: {
      search: {
        timestamp: {
          $between: [
            dayjs.tz(dateRange[0], 'America/New_York').toISOString(),
            dayjs.tz(dateRange[1], 'America/New_York').toISOString()
          ]
        },
        action: { $in: ['CREATED', 'UPDATED', 'DELETED'] }
      },
      page: 1,
      limit: 10,
      sort: [{
        field: 'timestamp',
        order: 'DESC'
      }],
    },
    expandRowComponent: ExchangeAuditDetailsComponent,
    entityIdParamName: 'id',
    authPermissions: authPermissions
  };
};

export const exchangeDealsDataMock2 = {
  data: {
    items: [
      {
        name: 'sagi_deal',
        dealStart: '2022-07-18T22:00:00.000Z',
        externalDealId: '1R-RX1-00187-20220724103432',
        dealId: 22040,
        agency: 'Yahoo',
        brand: 'Walmart',
        wseat: 'wseat1,wseat2',
        demandMarginValue: 25,
        dataFee: 2.6785,
        bidfloor: 3.3165,
        dealEnd: '2023-09-25T22:00:00.000Z',
        description: 'RX_Deal_Test-API_ver2',
        status: 'inactive',

        dspAccounts: [
          {
            id: 5069,
            name: 'Tremor',
          },
          {
            id: 5014,
            name: 'Mediamath',
          }
        ],
        ssps: [
          {
            id: 2093,
            name: 'IronSource',
          },
          {
            id: 2091,
            name: 'Taboola',
          }
        ],
        viewabilityPercentage: 33,
        impType: 'video',
        mediaType: [
          'site',
          'app'
        ],
        createdAt: '2022-07-24T22:34:32.788Z',
        updatedAt: null,
        deviceType: [
          'desktop',
          'mobile'
        ],
        geoTargeting: {
          countries: [
            'US'
          ],
          regions: [
            'al',
            'de'
          ],
          metros: [
            'new york',
            'detroit'
          ],
          zipCodes: [
            'A1A2A3',
            'B1B2B3'
          ]
        }
      },
      {
        name: 'sagi_deal_2',
        dealStart: '2022-09-19T22:00:00.000Z',
        externalDealId: '1R-RX1-09187-20220712345678',
        dealId: 22045,
        agency: 'Yahoo',
        brand: 'Walmart',
        wseat: 'wseat1,wseat2',
        demandMarginValue: 25,
        dataFee: 2.6785,
        bidfloor: 3.3165,
        dealEnd: '2023-09-25T22:00:00.000Z',
        description: 'RX_Deal_Test-API_ver2',
        status: 'active',

        dspAccounts: [
          {
            id: 5069,
            name: 'Tremor',
          },
          {
            id: 5014,
            name: 'Mediamath',
          }
        ],
        sspAccounts: [
          {
            id: 2093,
            name: 'IronSource',
          },
          {
            id: 2091,
            name: 'Taboola',
          }
        ],
        viewabilityPercentage: 33,
        impType: 'video',
        mediaType: [
          'site',
          'app'
        ],
        createdAt: '2022-07-24T22:34:32.788Z',
        updatedAt: null,
        deviceType: [
          'desktop',
          'mobile'
        ],
        geoTargeting: {
          countries: [
            'US'
          ],
          regions: [
            'al',
            'de'
          ],
          metros: [
            'new york',
            'detroit'
          ],
          zipCodes: [
            'A1A2A3',
            'B1B2B3'
          ]
        }
      }
    ],
    meta: {
      totalItems: 2,
      itemCount: 2,
      itemsPerPage: 10,
      totalPages: 1,
      currentPage: 1
    }
  }
};





